import AjaxService from '@services/AjaxService';
import UrlParameterInterface from '@repositories/UrlParameterInterface';
import BaseRepository, {SelectOption} from '@repositories/desktop/BaseRepository';

export default class UserRepository extends BaseRepository {
  protected baseUrl = '';

  public constructor(protected readonly ajaxService: AjaxService) {
    super(ajaxService)
  }

  public changeMainFirmBranch(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/main-firm-branch`,
      {data: {attributes}}
    );
  }

  public create(attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      '/api/desktop/users/create',
      {data: {attributes}}
    );
  }

  public delete(userId: number): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/users/${userId}/delete`
    );
  }

  public deleteCredentials(userId: number, credentialKey: string): Promise<any> {
    return this.ajaxService.sendDelete(
      `/api/desktop/users/${userId}/credentials/${credentialKey}/delete`
    );
  }

  public getAccessibleFirmBranches(parameters: UrlParameterInterface): Promise<any> {
    const url = '/api/desktop/users/firm-branches';
    return this.ajaxService.getJsonWithParameters(url, parameters);
  }

  public getBlank(): Promise<any> {
    return this.ajaxService.getJson('/api/desktop/users/blank');
  }

  public getFirmOptions<T = number | string>(
    resellerId?: number
  ): Promise<Array<SelectOption<T>>> {
    const urlParameters: UrlParameterInterface|undefined = resellerId ? {resellerId: resellerId} : undefined;

    return this.ajaxService.getJsonWithParameters(
      '/api/desktop/users/options/firm',
      urlParameters
    ).then(response => {
      return response.data.attributes.options as Array<SelectOption<T>>
    });
  }

  public getMainFirmBranchOptions<T = number | string>(
    userId: number
  ): Promise<Array<SelectOption<T>>> {
    const urlParameters: UrlParameterInterface = {userId: userId};

    return this.ajaxService.getJsonWithParameters(
      '/api/desktop/users/options/main-firm-branch',
      urlParameters
    ).then(response => {
      return response.data.attributes.options as Array<SelectOption<T>>
    });
  }

  public getReceiptAddressNumbersAccessibleOptions(userId: number): Promise<any> {
    return this.loadSelectOptions(`/api/desktop/users/${userId}/basedata/receipt-address-numbers-accessible/options`)
  }

  public loadUserCredentials(userId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/users/${userId}/credentials`);
  }

  public loadUserOverview(userId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/users/${userId}`);
  }

  public loadUserSubstitute(userId: number): Promise<any> {
    return this.ajaxService.getJson(`/api/desktop/users/${userId}/substitute`);
  }

  public reloadUserCreateBasedata(attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      '/api/desktop/users/reload',
      {data: {attributes}}
    );
  }

  public resetOtpFailureCount(userId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/reset-otp-failure-count`,
      {}
    );
  }

  public resetPassword(userId: number): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/reset-password`,
      {}
    );
  }

  public saveBasedata(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/basedata/save`,
      {data: {attributes}}
    );
  }

  public saveCredentialsData(userId: number, credentialKey: string, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/credentials/${credentialKey}/save`,
      {data: {attributes}}
    );
  }

  public saveFiltersData(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/filters/save`,
      {data: {attributes}}
    );
  }

  public saveFirmBranchData(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/firm-branches/save`,
      {data: {attributes}}
    );
  }

  public saveFirmLevelData(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/firm-levels/save`,
      {data: {attributes}}
    );
  }

  public saveSignatureData(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/signature/save`,
      {data: {attributes}}
    );
  }

  public saveUserSubstitute(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/substitute/save`,
      {data: {attributes}}
    );
  }

  public saveWebserviceData(userId: number, attributes: any): Promise<any> {
    return this.ajaxService.postJson(
      `/api/desktop/users/${userId}/webservices/save`,
      {data: {attributes}}
    );
  }

  public showCurrentUser(): Promise<any> {
    return this.ajaxService.getJson('/api/users/current');
  }
}
